<template>
  <div>
    <McButton
      :disabled="disabled"
      :variant="variant"
      :text="text"
      :icon="icon"
      :size="size"
      @click="handleOpenModal"
    />

    <b-modal
      v-model="modalShown"
      refs="modalRef"
      centered
      size="lg"
      :title="modalTitle || `${label}`"
      :ok-title="okTitle || $t('global.apply')"
      :cancel-title="cancelTitle || $t('global.cancel')"
      header-close-content=""
      cancel-variant="outline-primary"
      @hidden="handleHide"
      @ok="handleApply"
    >
      <div class="mc-value-help">
        <div class="__table-top">
          <h6 v-if="multiple && selectedValuesLength" class="mc-row-check mt-0">
            <span class="badge btn-success">{{ selectedValuesLength }}</span>
            {{ $t('global.object.selectedRow') }}
            <a class="color-primary" @click="handleUnselectAll">{{
              $t('global.action.deselect')
            }}</a>
          </h6>

          <b-input-group size="sm" class="mc-has-search mc-input" @click="focusSearch">
            <template #prepend>
              <i class="mc-icon icon-fill_search"></i>
            </template>

            <b-form-input
              ref="searchInput"
              :value="search"
              :disabled="isBusy"
              :placeholder="$t('global.search') + '...'"
              @input="handleSearchChange"
            />

            <template v-if="search" #append>
              <button
                class="clear-search-btn"
                :disabled="isBusy"
                @click="handleClearSearch"
              ></button>
            </template>
          </b-input-group>
        </div>
        <div ref="tableWrapperRef" class="__table-wrapper">
          <b-table
            ref="tableRef"
            show-empty
            selectable
            :select-mode="multiple ? 'multi' : 'single'"
            :fields="computedTableFields"
            :items="items"
            :busy="isBusy"
            class="-w-auto text-nowrap sticky-head-table border"
            @row-clicked="handleRowClicked"
          >
            <template #empty>
              <div v-show="!isBusy" class="text-center my-4">
                {{ $t('global.text.noData') }}
              </div>
            </template>

            <template #head(checkbox)>
              <div v-show="hasSelectAll" class="form-check" style="width: 10px">
                <input
                  v-model="selectAll"
                  class="form-check-input me-1"
                  type="checkbox"
                  value="all"
                  @change="handleToggleAll"
                />
              </div>
            </template>

            <template #cell(checkbox)="{ index, item }">
              <div class="form-check">
                <b-form-checkbox
                  v-model="selectedId"
                  :value="item[trackBy]"
                  @change="handleCheckboxChange(item, index)"
                />
              </div>
            </template>

            <template #head()="data">
              <div @click="handleSortChange(data)">
                {{ data.label }}
                <template v-if="data.field.isSortable">
                  <div v-if="showSortIcon(data, 'asc')" class="sort-icon-up">
                    <i class="mc-icon icon-outline_chevron-up"></i>
                  </div>
                  <div v-if="showSortIcon(data, 'desc')" class="sort-icon-down">
                    <i class="mc-icon icon-outline_chevron-down"></i>
                  </div>
                </template>
              </div>
            </template>

            <!-- Slot: All Cells -->
            <template #cell()="{ field, value }">
              <template v-if="field.hasBadge">
                <McBadge :text="value.label" :variant="value.color" type="status" class="mx-2" />
              </template>
              <template v-else>
                {{ field.hasBadge }}
                {{ value }}
              </template>
            </template>
          </b-table>
          <div v-observe-visibility="handleElementObserved" style="height: 1px"></div>
          <div v-if="isBusy && hasNextPage" class="px-2 py-2 d-flex gap-2">
            <b-skeleton width="2.5%" />
            <b-skeleton width="97.5%" />
          </div>
        </div>

        <div class="__data-count">
          {{ $t('global.text.displays') }} <strong>{{ totalCount || items.length }}</strong>
          {{ $t('global.text.data') }}
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import { debounce } from '@/helpers/debounce'
import McButton from '@/components/McButton'
import McBadge from '@/components/McBadge'

export default {
  components: {
    McButton,
    McBadge,
  },
  props: {
    modalTitle: { type: String },
    label: { type: String },
    okTitle: { type: String },
    cancelTitle: { type: String },
    multiple: { type: Boolean },
    trackBy: { type: String, default: 'id' },
    tableFields: { type: Array },
    listGetter: { required: true },
    disabled: { type: Boolean },
    variant: {
      default: 'primary',
    },
    text: {},
    icon: {},
    size: {},
    hasPopup: { type: Boolean, default: false },
    popupId: {},
    hasSelectAll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectedValues: {},
      selectedId: [],
      modalShown: false,
      items: [],
      isBusy: true,
      unsavedModel: null,
      ids: [],
      page: 1,
      limit: 10,
      totalCount: null,
      search: '',
      sorts: [],
      selectAll: false,
      exclude: [],
    }
  },
  computed: {
    computedTableFields() {
      if (!this.tableFields) return []

      const tableFields = [...this.tableFields]
      if (this.multiple) {
        tableFields.unshift({
          key: 'checkbox',
          label: '',
        })
      }

      return tableFields
    },
    hasNextPage() {
      if (!this.totalCount) return true
      return this.page * this.limit < this.totalCount
    },
    selectedValuesLength() {
      if (this.selectAll) return this.totalCount - this.exclude.length
      else return this.selectedId.length
    },
  },
  methods: {
    focusSearch() {
      this.$refs.searchInput.focus()
    },
    handleOpenModal() {
      if (this.disabled) return
      this.modalShown = true
    },
    async getData(opts, reset = false) {
      this.isBusy = true

      if (!this.listGetter) {
        this.isBusy = false
        return
      }

      const res = await this.listGetter(opts)
      if (!res) return

      this.items = reset ? res.data.paginated_result : [...this.items, ...res.data.paginated_result]

      this.ids = this.items.map((item) => item[this.trackBy])
      this.page = res.metadata.page
      this.totalCount = res.metadata.total_count
      if (this.selectAll) {
        this.handleSelectAll()
        if (this.exclude) {
          this.exclude.map((item) => {
            this.$delete(this.selectedValues, item)
            this.selectedId = this.selectedId.filter((val) => val !== item)
            this.selected = this.selectedId
          })
        }
      }

      this.isBusy = false

      await this.$nextTick()
      this.highlightSelectedRow()
    },

    highlightSelectedRow() {
      const trackBy = this.trackBy
      this.selectedId.forEach((id) => {
        const index = this.items.findIndex((item) => item[trackBy] == id)
        // Toggle the row checkbox

        if (index !== -1 && this.$refs.tableRef) {
          this.$refs.tableRef.selectRow(index)
        }
      })
    },
    handleElementObserved(isVisible) {
      if (!isVisible) return
      if (!this.hasNextPage) return

      this.getData({
        limit: this.limit,
        page: this.totalCount ? ++this.page : this.page,
        search: this.search != '' ? this.search : undefined,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      })

      const componentRef = this.$refs.tableWrapperRef
      this.$nextTick().then(() => {
        componentRef.scrollTo?.({
          top: componentRef.scrollHeight,
          behavior: 'smooth',
        })
      })
    },

    checkSelectAll() {
      if (this.selectAll) {
        if (this.exclude.length === this.items.length) {
          this.selectAll = false
          this.exclude = []
        }
      }
    },

    assignSelectedValue(checked, item) {
      if (checked) {
        this.$delete(this.selectedValues, item[this.trackBy])
        this.selected = this.selectedId
        this.checkSelectAll()
        return
      }
      this.$set(this.selectedValues, item[this.trackBy], item)
      this.selected = this.selectedId
      this.checkSelectAll()
    },

    handleRowClicked(item, index) {
      if (!this.multiple) {
        this.selectedId = []
        this.selectedValues = {}
      }

      const trackBy = this.trackBy
      const checked = this.selectedId.find((val) => val == item[trackBy])
      const sectionCheck = checked ? 'selectRow' : 'unselectRow'
      this.$refs.tableRef[sectionCheck](index)
      if (checked) {
        this.selectedId = this.selectedId.filter((val) => val !== item[trackBy])
        if (this.selectAll) {
          this.exclude.push(item[this.trackBy])
        }
      } else {
        this.selectedId.push(item[trackBy])
        if (this.selectAll) this.exclude = this.exclude.filter((id) => id !== item[trackBy])
      }

      this.assignSelectedValue(checked, item)
    },
    handleCheckboxChange(item, index) {
      if (!this.multiple) {
        this.selectedValues = {}
      }

      const trackBy = this.trackBy
      const checked = this.selectedId.find((val) => val == item[trackBy])
      if (checked) {
        if (this.selectAll) {
          this.exclude = this.exclude.filter((id) => id !== item[trackBy])
        }
      } else {
        if (this.selectAll) {
          if (this.exclude.includes(item[trackBy])) {
            this.exclude = this.exclude.filter((id) => id !== item[trackBy])
          }
          this.exclude.push(item[this.trackBy])
        }
      }

      const sectionCheck = checked ? 'selectRow' : 'unselectRow'
      this.$refs.tableRef[sectionCheck](index)
      this.assignSelectedValue(!checked, item)
    },
    handleUnselectAll() {
      this.selectedValues = {}
      this.selectedId = []
      this.$refs.tableRef.clearSelected()
    },
    handleSelectAll() {
      if (this.trackBy === 'id') {
        this.items.forEach((item) => {
          if (!this.selectedId.includes(item[this.trackBy]))
            this.selectedId.push(item[this.trackBy])
          this.selected = this.selectedId

          this.$set(this.selectedValues, item[this.trackBy], item)
        })
        this.$refs['tableRef'].selectAllRows()
      } else {
        throw new Error('Select all is not yet implemented when trackBy is not id')
      }
    },
    handleToggleAll(event) {
      let checked = event.target.checked

      if (checked) {
        this.selectAll = true
        this.handleSelectAll()
      } else {
        this.selectAll = false
        this.handleUnselectAll()
      }
    },
    handleSearchChange: debounce(async function (_search) {
      this.search = _search
      const opts = {
        limit: this.limit,
        search: _search != '' ? _search : undefined,
        page: 1,
        sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
      }
      await this.getData(opts, true)
      this.$refs['searchInput'].$el.focus()
    }, 500),
    handleClearSearch() {
      this.handleSearchChange('')
    },
    showSortIcon(data, order) {
      const currentColumnSort = this.sorts.find((sort) => sort.key === data.field.key)
      if (!currentColumnSort) {
        return true
      }

      return currentColumnSort.key === data.field.key && currentColumnSort.order === order
    },
    async handleSortChange(data) {
      if (!data) {
        this.sorts = []
        return
      }

      if (!data?.field?.isSortable) return

      const currentColumnSortIndex = this.sorts.findIndex((sort) => sort.key === data.field.key)
      const currentColumnSort = this.sorts[currentColumnSortIndex]
      const newSorts = [...this.sorts]
      switch (currentColumnSort?.order) {
        case 'asc':
          newSorts[currentColumnSortIndex] = {
            key: currentColumnSort.key,
            order: 'desc',
          }
          break

        case 'desc':
          newSorts.splice(currentColumnSortIndex, 1)
          break

        default:
          newSorts.push({ key: data.field.key, order: 'asc' })
          break
      }

      this.sorts = [...newSorts]
      await this.$nextTick()

      await this.getData(
        {
          limit: this.limit,
          page: 1,
          search: this.search != '' ? this.search : undefined,
          sorts: this.sorts.map((sort) => `${sort.key} ${sort.order}`),
        },
        true
      )
    },
    async handleApply() {
      let trueSelected = Object.keys(this.selectedValues)
      let selectedIds = this.multiple
        ? this.selectedId
        : this.items.find((item) => item[this.trackBy] == Number(trueSelected[0]))

      if (this.selectAll) {
        this.$emit('exclude', this.exclude, this.selectAll)
      } else {
        this.$emit('selected', selectedIds)
      }

      this.unsavedModel = null
      if (this.hasPopup) this.$bvModal.hide(this.popupId)

      this.handleHide()
    },
    handleHide() {
      this.items = []
      this.ids = []
      this.page = 1
      this.totalCount = null
      this.sorts = []
      this.search = ''
      this.selectedId = []

      this.unsavedModel = null
      this.selectedValues = {}
      this.selectAll = false
      this.exclude = false
      this.selected = []
    },
  },
}
</script>

<style lang="scss" scoped>
@import './style.scss';
</style>
